import React, { useEffect, useState, Component } from 'react';
import { ArrowLeft, Clock, MapPin, Coffee, CreditCard, Gift, Heart, Sparkles, Users, Share2, Check, UserPlus, Copy, X } from 'lucide-react';
import { Store } from '../../types';
import { useToast } from '../ToastContext';
interface CheckInSuccessScreenProps {
  store: Store;
  onDismiss: () => void;
  onViewMenu?: () => void;
}
export function CheckInSuccessScreen({
  store,
  onDismiss,
  onViewMenu
}: CheckInSuccessScreenProps) {
  const [showInitialCard, setShowInitialCard] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const {
    showToast
  } = useToast();
  // Generate referral code
  const generateReferralCode = () => {
    const storePrefix = store.name.substring(0, 4).toUpperCase().replace(/\s/g, '');
    const randomHash = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${storePrefix}-${randomHash}`;
  };
  const handleRecommend = () => {
    const code = generateReferralCode();
    setReferralCode(code);
    setShowReferralModal(true);
  };
  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    showToast('Referral code copied!', 'success');
  };
  const handleShareWhatsApp = () => {
    const message = `Hey! Check out ${store.name}! Use my code ${referralCode} and we both get ₹100 credit! 🎁`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };
  // Flip to membership card after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);
  return <div className="flex flex-col h-full bg-[#B353F2] overflow-y-auto" data-id="element-218">
      {/* Header */}
      <div className="relative" data-id="element-219">
        {/* Back Button */}
        <button onClick={onDismiss} className="absolute top-6 left-4 z-10 bg-[#FFFC00] rounded-full p-3 shadow-lg hover:bg-[#F7C300] transition-colors" data-id="element-220">
          <ArrowLeft size={24} className="text-black" data-id="element-221" />
        </button>

        {/* Distance Badge */}
        <div className="absolute top-6 right-4 z-10" data-id="element-222">
          <div className="flex items-center bg-black/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full" data-id="element-223">
            <MapPin size={16} className="mr-1" data-id="element-224" />
            <span className="text-sm font-medium" data-id="element-225">{store.distance}</span>
          </div>
        </div>

        {/* Store Image with Time Overlay */}
        <div className="w-full h-48 relative" data-id="element-226">
          <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-227" />
          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white" data-id="element-228">
            <div className="flex items-center gap-3 mb-2" data-id="element-229">
              <Clock size={24} className="text-white" data-id="element-230" />
              <span className="text-3xl font-light" data-id="element-231">{store.hours}</span>
            </div>
            <span className="text-xl font-light" data-id="element-232">Open Now</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-6 pb-32" data-id="element-233">
        {/* Store Info */}
        <div className="mb-6" data-id="element-234">
          <h1 className="text-4xl font-bold text-white mb-3" data-id="element-235">{store.name}</h1>
          <div className="flex gap-2 flex-wrap" data-id="element-236">
            {store.tags.map(tag => <span key={tag} className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium" data-id="element-237">
                {tag}
              </span>)}
          </div>
        </div>

        {/* Flip Card: Success Message → Membership Card */}
        <div className="relative h-32 mb-6" style={{
        perspective: '1000px'
      }} data-id="element-238">
          <div className="relative w-full h-full transition-all duration-700 ease-in-out" style={{
          transformStyle: 'preserve-3d',
          transform: showSuccessMessage ? 'rotateY(0deg)' : 'rotateY(180deg)'
        }} data-id="element-239">
            {/* Front: Success Message */}
            <div className="absolute inset-0 bg-[#E6FFE6] rounded-xl p-4 animate-in fade-in slide-in-from-top duration-500" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }} data-id="element-240">
              <div className="flex items-start h-full" data-id="element-241">
                <div className="w-6 h-6 rounded-full bg-[#4CD964] flex items-center justify-center mr-3 mt-0.5 flex-shrink-0" data-id="element-242">
                  <Check size={14} className="text-white" strokeWidth={3} data-id="element-243" />
                </div>
                <div data-id="element-244">
                  <h2 className="text-lg font-semibold text-[#4CD964] mb-1" data-id="element-245">
                    Check-in successful!
                  </h2>
                  <p className="text-[#4CD964]" data-id="element-246">
                    You're now being tracked for rewards
                  </p>
                </div>
              </div>
            </div>

            {/* Back: Membership Card */}
            <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-600 to-amber-500 rounded-xl p-4" style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }} data-id="element-247">
              <div className="h-full flex flex-col justify-between" data-id="element-248">
                <div data-id="element-249">
                  <div className="flex items-center gap-2 mb-2" data-id="element-250">
                    <div className="text-2xl" data-id="element-251">👑</div>
                    <div data-id="element-252">
                      <h3 className="text-amber-200 font-bold text-base leading-tight" data-id="element-253">
                        MAHARAJA MEMBERSHIP
                      </h3>
                      <p className="text-amber-100/80 text-xs" data-id="element-254">
                        Royal treatment, every visit
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={() => showToast('Maharaja Membership coming soon! 👑', 'info')} className="w-full bg-gradient-to-r from-amber-400 to-yellow-300 text-red-900 py-2.5 rounded-lg font-bold text-sm hover:from-amber-300 hover:to-yellow-200 transition-all active:scale-95 shadow-lg" data-id="element-255">
                  Join the Royal Circle - ₹999/year
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* STAGE 1: How It Works Card */}
        {showInitialCard ? <div className="bg-[#FFFC00] rounded-xl p-5 animate-in fade-in slide-in-from-bottom duration-500" data-id="element-256">
            <h2 className="text-lg font-bold mb-4 text-black" data-id="element-257">How It Works</h2>

            {/* Three Steps */}
            <div className="space-y-4 mb-6" data-id="element-258">
              <div className="flex items-start" data-id="element-259">
                <Coffee size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-260" />
                <p className="text-sm text-black" data-id="element-261">
                  Order like you normally would
                </p>
              </div>
              <div className="flex items-start" data-id="element-262">
                <CreditCard size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-263" />
                <p className="text-sm text-black" data-id="element-264">
                  Pay using your linked card or UPI
                </p>
              </div>
              <div className="flex items-start" data-id="element-265">
                <Gift size={20} className="text-black mr-3 mt-0.5 flex-shrink-0" data-id="element-266" />
                <p className="text-sm text-black" data-id="element-267">
                  If a reward is active, it'll get added automatically after
                  payment
                </p>
              </div>
            </div>

            {/* Encouragement Text */}
            <p className="text-center text-black/70 text-sm italic mb-4" data-id="element-268">
              Now sit back & relax, enjoy your visit!
            </p>

            {/* Got It Button */}
            <button onClick={() => setShowInitialCard(false)} className="w-full bg-black text-white py-3.5 rounded-xl font-medium hover:bg-black/90 transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200" data-id="element-269">
              Got it
            </button>
          </div> /* STAGE 2: Action Cards After "Got it" */ : <div className="space-y-4 animate-in fade-in slide-in-from-bottom duration-500" data-id="element-270">
            {/* Quick Order Favorites */}
            <div className="bg-[#F0EEFF] rounded-xl p-4 hover:bg-[#E5D9FF] transition-colors cursor-pointer group" onClick={() => showToast("Quick orders coming soon! We'll keep you posted 🚀", 'info')} data-id="element-271">
              <div className="flex items-center justify-between" data-id="element-272">
                <div className="flex items-center flex-1" data-id="element-273">
                  <div className="w-10 h-10 rounded-full bg-[#FFFC00] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-274">
                    <Heart size={20} className="text-black" data-id="element-275" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-276">
                    <h3 className="font-semibold text-black" data-id="element-277">
                      Quick Order Favorites
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-278">
                      Your usual: Latte, Croissant
                    </p>
                  </div>
                </div>
                <span className="text-[#A020F0] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-279">
                  Order →
                </span>
              </div>
            </div>

            {/* Today's Specials */}
            <div className="bg-[#FFF3E0] rounded-xl p-4 hover:bg-[#FFE0B2] transition-colors cursor-pointer group" onClick={() => showToast("Today's specials coming soon! Stay tuned 🎉", 'info')} data-id="element-280">
              <div className="flex items-center justify-between" data-id="element-281">
                <div className="flex items-center flex-1" data-id="element-282">
                  <div className="w-10 h-10 rounded-full bg-[#FF9800] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-283">
                    <Sparkles size={20} className="text-white" data-id="element-284" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-285">
                    <h3 className="font-semibold text-black" data-id="element-286">
                      Today's Specials
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-287">2 new items added</p>
                  </div>
                </div>
                <span className="text-[#FF9800] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-288">
                  View →
                </span>
              </div>
            </div>

            {/* Skip the Line */}
            <div className="bg-[#E8F5E9] rounded-xl p-4 hover:bg-[#C8E6C9] transition-colors cursor-pointer group" onClick={() => showToast("Skip the line feature coming soon! We'll notify you 🔔", 'info')} data-id="element-289">
              <div className="flex items-center justify-between" data-id="element-290">
                <div className="flex items-center flex-1" data-id="element-291">
                  <div className="w-10 h-10 rounded-full bg-[#4CAF50] flex items-center justify-center mr-3 flex-shrink-0" data-id="element-292">
                    <Users size={20} className="text-white" data-id="element-293" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-294">
                    <h3 className="font-semibold text-black" data-id="element-295">Skip the line</h3>
                    <p className="text-sm text-gray-600" data-id="element-296">Is it too crowded?</p>
                  </div>
                </div>
                <span className="text-[#4CAF50] text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-297">
                  Skip →
                </span>
              </div>
            </div>

            {/* Share Check-in */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl p-4 hover:from-indigo-600 hover:to-purple-600 transition-colors cursor-pointer group" onClick={handleRecommend} data-id="element-298">
              <div className="flex items-center justify-between" data-id="element-299">
                <div className="flex items-center flex-1" data-id="element-300">
                  <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mr-3 flex-shrink-0" data-id="element-301">
                    <UserPlus size={20} className="text-white" data-id="element-302" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-303">
                    <h3 className="font-semibold text-white" data-id="element-304">
                      Recommend this place
                    </h3>
                    <p className="text-xs text-white/80" data-id="element-305">
                      Share the love with friends, both get ₹100 credit
                    </p>
                  </div>
                </div>
                <span className="text-white text-sm font-medium group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" data-id="element-306">
                  Refer →
                </span>
              </div>
            </div>

            {/* Done Button */}
            <button onClick={onDismiss} className="w-full bg-[#FFFC00] text-black py-3.5 rounded-xl font-semibold hover:bg-[#F7C300] transition-all transform hover:scale-[1.02] active:scale-[0.98] duration-200 mt-6" data-id="element-307">
              Done
            </button>
          </div>}
      </div>

      {/* Referral Modal */}
      {showReferralModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setShowReferralModal(false)} data-id="element-308">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()} data-id="element-309">
            {/* Header */}
            <div className="flex items-center justify-between mb-4" data-id="element-310">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2" data-id="element-311">
                <Gift size={24} className="text-indigo-600" data-id="element-312" />
                Share & Earn Together!
              </h3>
              <button onClick={() => setShowReferralModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-313">
                <X size={20} className="text-gray-600" data-id="element-314" />
              </button>
            </div>

            {/* Referral Code */}
            <div className="mb-6" data-id="element-315">
              <p className="text-sm text-gray-600 mb-2" data-id="element-316">Your Referral Code:</p>
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-4 flex items-center justify-between" data-id="element-317">
                <span className="font-mono font-bold text-2xl text-indigo-600" data-id="element-318">
                  {referralCode}
                </span>
                <button onClick={handleCopyCode} className="p-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all active:scale-95" title="Copy code" data-id="element-319">
                  <Copy size={18} data-id="element-320" />
                </button>
              </div>
            </div>

            {/* Perks */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 mb-6" data-id="element-321">
              <p className="text-sm font-semibold text-gray-900 mb-3" data-id="element-322">
                When your friend uses this code:
              </p>
              <div className="space-y-2 text-sm text-gray-700" data-id="element-323">
                <div className="flex items-start gap-2" data-id="element-324">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-325" />
                  <span data-id="element-326">
                    You get <strong data-id="element-327">₹100 store credit</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-328">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-329" />
                  <span data-id="element-330">
                    They get <strong data-id="element-331">₹50 welcome credit</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-332">
                  <Check size={16} className="text-indigo-600 flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-333" />
                  <span data-id="element-334">
                    Both earn <strong data-id="element-335">bonus loyalty stamps</strong>
                  </span>
                </div>
              </div>
            </div>

            {/* Share Options */}
            <div className="space-y-3" data-id="element-336">
              <p className="text-sm font-semibold text-gray-900" data-id="element-337">Share via:</p>
              <button onClick={handleShareWhatsApp} className="w-full bg-[#25D366] text-white py-3 rounded-xl font-semibold hover:bg-[#20BA5A] transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-338">
                <Share2 size={18} data-id="element-339" />
                WhatsApp
              </button>
              <button onClick={handleCopyCode} className="w-full bg-gray-100 text-gray-900 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-340">
                <Copy size={18} data-id="element-341" />
                Copy Code
              </button>
            </div>

            {/* Close Button */}
            <button onClick={() => setShowReferralModal(false)} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all active:scale-[0.98] mt-4" data-id="element-342">
              Done
            </button>
          </div>
        </div>}
    </div>;
}